<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore, useFunctionGetter } from 'dashboard/composables/store';
import sallaAPI from 'dashboard/api/salla';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const integration = useFunctionGetter('integrations/getIntegration', 'salla');

const sallaLogoUrl = '/dashboard/images/integrations/salla.png';
const isLoading = ref(true);
const isWorking = ref(false);
const activeTab = ref('customers');
const status = ref({});
const keyword = ref('');
const defaultInboxId = ref('');
const eventInput = ref('order.created,order.updated,product.created,product.updated,customer.created,customer.updated');
const webhookEvents = ref([]);

const resources = reactive({
  customers: { page: 1, payload: null, loading: false },
  products: { page: 1, payload: null, loading: false },
  orders: { page: 1, payload: null, loading: false },
  carts: { page: 1, payload: null, loading: false },
});

const tabs = computed(() => [
  { key: 'customers', label: t('INTEGRATION_SETTINGS.SALLA.TABS.CUSTOMERS') },
  { key: 'products', label: t('INTEGRATION_SETTINGS.SALLA.TABS.PRODUCTS') },
  { key: 'orders', label: t('INTEGRATION_SETTINGS.SALLA.TABS.ORDERS') },
  { key: 'carts', label: t('INTEGRATION_SETTINGS.SALLA.TABS.CARTS') },
]);

const connected = computed(() => !!status.value.connected);
const configured = computed(() => !!status.value.configured);
const currentResource = computed(() => resources[activeTab.value]);
const currentItems = computed(() => extractItems(currentResource.value.payload));
const merchantName = computed(() => {
  const merchant = status.value.merchant;
  if (!merchant) return '';
  return merchant.name || merchant.store?.name || merchant.data?.name || merchant.data?.store?.name || '';
});

const extractItems = payload => {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.data)) return payload.data.data;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

const resourceRowKey = (record, index) =>
  record.id || record.reference_id || record.reference || `${activeTab.value}-${currentResource.value.page}-${index}`;

const resourceTitle = record =>
  record.name || record.full_name || record.reference_id || record.reference || record.sku || record.id || '-';

const resourceDetail = record => {
  if (activeTab.value === 'customers') return record.email || record.mobile || record.phone || '-';
  if (activeTab.value === 'products') return record.sku || record.status || record.price?.amount || '-';
  if (activeTab.value === 'orders') return record.customer?.name || record.customer?.email || record.status?.name || record.status || '-';
  return record.customer?.name || record.customer?.email || record.total?.amount || '-';
};

const resourceStatus = record => record.status?.name || record.status || record.type || record.created_at || '-';

const hasNextPage = computed(() => currentItems.value.length > 0);
const inboxOptions = computed(() => inboxes.value || []);

const loadStatus = async () => {
  const { data } = await sallaAPI.status();
  status.value = data;
  defaultInboxId.value = data.default_inbox_id || '';
};

const redirectIfServiceDisabled = () => {
  if (status.value.tenant_enabled !== false) return false;

  router.replace({ path: route.path.replace(/\/[^/]+\/?$/, '') });
  return true;
};

const loadResource = async (type = activeTab.value) => {
  if (!connected.value) return;
  const resource = resources[type];
  resource.loading = true;
  try {
    const params = { page: resource.page, per_page: 25, keyword: keyword.value };
    const requestMap = {
      customers: () => sallaAPI.customers(params),
      products: () => sallaAPI.products(params),
      orders: () => sallaAPI.orders(params),
      carts: () => sallaAPI.abandonedCarts(params),
    };
    const { data } = await requestMap[type]();
    resource.payload = data;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    resource.loading = false;
  }
};

const connectSalla = async () => {
  isWorking.value = true;
  try {
    const { data } = await sallaAPI.authUrl();
    window.location.href = data.url;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const disconnectSalla = async () => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.SALLA.DISCONNECT_CONFIRM'))) return;
  isWorking.value = true;
  try {
    await sallaAPI.disconnect();
    await store.dispatch('integrations/get');
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.SALLA.API.DISCONNECTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const setPage = async direction => {
  const resource = currentResource.value;
  const nextPage = resource.page + direction;
  if (nextPage < 1) return;
  resource.page = nextPage;
  await loadResource();
};

const refreshCurrentTab = async () => {
  currentResource.value.page = 1;
  await loadResource();
};

const saveSettings = async () => {
  isWorking.value = true;
  try {
    await sallaAPI.updateSettings({ default_inbox_id: defaultInboxId.value });
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.SALLA.API.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const loadWebhookEvents = async () => {
  isWorking.value = true;
  try {
    const { data } = await sallaAPI.webhookEvents({ per_page: 100 });
    webhookEvents.value = extractItems(data);
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const registerWebhooks = async () => {
  isWorking.value = true;
  try {
    await sallaAPI.registerWebhooks({
      events: eventInput.value,
      default_inbox_id: defaultInboxId.value,
    });
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.SALLA.API.WEBHOOKS_REGISTERED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const importCustomer = async customer => {
  isWorking.value = true;
  try {
    await sallaAPI.importCustomer(customer);
    useAlert(t('INTEGRATION_SETTINGS.SALLA.API.CUSTOMER_IMPORTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const createConversation = async resourceRecord => {
  if (!defaultInboxId.value) {
    useAlert(t('INTEGRATION_SETTINGS.SALLA.SELECT_INBOX_FIRST'));
    return;
  }
  isWorking.value = true;
  try {
    await sallaAPI.createConversation({
      resource_type: activeTab.value,
      resource: resourceRecord,
      inbox_id: defaultInboxId.value,
    });
    useAlert(t('INTEGRATION_SETTINGS.SALLA.API.CONVERSATION_CREATED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.SALLA.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const initialize = async () => {
  try {
    await Promise.all([store.dispatch('integrations/get'), store.dispatch('inboxes/get')]);
    await loadStatus();
    if (redirectIfServiceDisabled()) return;
    if (route.query.connected) useAlert(t('INTEGRATION_SETTINGS.SALLA.API.CONNECTED'));
    if (route.query.error) useAlert(route.query.error);
    if (connected.value) await loadResource(activeTab.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(initialize);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.SALLA.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.SALLA.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5">
        <section class="flex flex-col gap-4 rounded-lg outline outline-1 outline-n-container bg-n-card p-5 md:flex-row md:items-center md:justify-between">
          <div class="flex gap-4">
            <img :src="sallaLogoUrl" class="h-14 w-14 rounded-lg border border-n-weak bg-n-alpha-2" alt="Salla" />
            <div>
              <h3 class="text-heading-2 text-n-slate-12">{{ integration.name || 'Salla' }}</h3>
              <p class="mt-1 text-body-main text-n-slate-11">
                {{ integration.description || $t('INTEGRATION_SETTINGS.SALLA.DESCRIPTION') }}
              </p>
              <p v-if="merchantName" class="mt-2 text-sm text-n-teal-11">
                {{ $t('INTEGRATION_SETTINGS.SALLA.CONNECTED_TO', { merchant: merchantName }) }}
              </p>
              <p v-else-if="!configured" class="mt-2 text-sm text-n-ruby-11">
                {{ $t('INTEGRATION_SETTINGS.SALLA.NOT_CONFIGURED') }}
              </p>
            </div>
          </div>
          <div class="flex gap-2">
            <Button v-if="connected" faded ruby :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.SALLA.DISCONNECT')" @click="disconnectSalla" />
            <Button v-else :disabled="!configured" :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.SALLA.CONNECT')" @click="connectSalla" />
          </div>
        </section>

        <section v-if="connected" class="rounded-lg outline outline-1 outline-n-container bg-n-card p-4">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.SALLA.DEFAULT_INBOX') }}</span>
              <select v-model="defaultInboxId" class="h-10 rounded-md border border-n-weak bg-n-solid-2 px-3">
                <option value="">{{ $t('INTEGRATION_SETTINGS.SALLA.NO_INBOX') }}</option>
                <option v-for="inbox in inboxOptions" :key="inbox.id" :value="inbox.id">{{ inbox.name }}</option>
              </select>
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              <span>{{ $t('INTEGRATION_SETTINGS.SALLA.WEBHOOK_EVENTS') }}</span>
              <Input v-model="eventInput" />
            </label>
          </div>
          <div class="mt-4 flex flex-wrap gap-2">
            <Button faded slate :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.SALLA.SAVE_SETTINGS')" @click="saveSettings" />
            <Button faded blue :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.SALLA.LOAD_EVENTS')" @click="loadWebhookEvents" />
            <Button :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.SALLA.REGISTER_WEBHOOKS')" @click="registerWebhooks" />
          </div>
          <div v-if="webhookEvents.length" class="mt-3 text-sm text-n-slate-11">
            {{ webhookEvents.slice(0, 12).map(event => event.name || event.event || event).join(', ') }}
          </div>
        </section>

        <section v-if="connected" class="rounded-lg outline outline-1 outline-n-container bg-n-card">
          <div class="flex flex-wrap gap-2 border-b border-n-weak p-3">
            <Button
              v-for="tab in tabs"
              :key="tab.key"
              size="sm"
              :variant="activeTab === tab.key ? 'solid' : 'faded'"
              color="slate"
              :label="tab.label"
              @click="activeTab = tab.key; loadResource(tab.key)"
            />
          </div>

          <div class="flex gap-2 p-4">
            <Input v-model="keyword" :placeholder="$t('INTEGRATION_SETTINGS.SALLA.SEARCH_PLACEHOLDER')" class="max-w-md" @keyup.enter="refreshCurrentTab" />
            <Button faded slate :label="$t('INTEGRATION_SETTINGS.SALLA.SEARCH')" @click="refreshCurrentTab" />
          </div>

          <div class="overflow-x-auto p-4 pt-0">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.SALLA.TABLE.NAME') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.SALLA.TABLE.DETAIL') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.SALLA.TABLE.STATUS') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.SALLA.TABLE.ACTIONS') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-12">
                <tr v-if="currentResource.loading">
                  <td colspan="4" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.SALLA.LOADING') }}</td>
                </tr>
                <template v-else>
                  <tr v-for="(record, index) in currentItems" :key="resourceRowKey(record, index)">
                    <td class="px-3 py-3">{{ resourceTitle(record) }}</td>
                    <td class="px-3 py-3 text-n-slate-11">{{ resourceDetail(record) }}</td>
                    <td class="px-3 py-3 text-n-slate-11">{{ resourceStatus(record) }}</td>
                    <td class="px-3 py-3">
                      <div class="flex flex-wrap gap-2">
                        <Button v-if="activeTab === 'customers'" xs faded blue :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.SALLA.IMPORT_CUSTOMER')" @click="importCustomer(record)" />
                        <Button v-if="activeTab === 'orders' || activeTab === 'carts'" xs faded slate :is-loading="isWorking" :label="$t('INTEGRATION_SETTINGS.SALLA.CREATE_CONVERSATION')" @click="createConversation(record)" />
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!currentResource.loading && !currentItems.length">
                  <td colspan="4" class="px-3 py-8 text-center text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.SALLA.EMPTY') }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="flex items-center justify-between border-t border-n-weak p-4">
            <span class="text-sm text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.SALLA.PAGE', { page: currentResource.page }) }}</span>
            <div class="flex gap-2">
              <Button xs faded slate :disabled="currentResource.page <= 1 || currentResource.loading" :label="$t('INTEGRATION_SETTINGS.SALLA.PREVIOUS')" @click="setPage(-1)" />
              <Button xs faded slate :disabled="!hasNextPage || currentResource.loading" :label="$t('INTEGRATION_SETTINGS.SALLA.NEXT')" @click="setPage(1)" />
            </div>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
